export interface AudioParamLike {
  setValueAtTime(value: number, time: number): void;
  exponentialRampToValueAtTime(value: number, time: number): void;
}

export interface OscillatorLike {
  type: OscillatorType;
  frequency: AudioParamLike;
  connect(node: unknown): void;
  start(time?: number): void;
  stop(time?: number): void;
}

export interface GainLike {
  gain: AudioParamLike;
  connect(node: unknown): void;
}

export interface AudioContextLike {
  state: string;
  currentTime: number;
  destination: unknown;
  resume(): Promise<void>;
  createOscillator(): OscillatorLike;
  createGain(): GainLike;
}

export type SoundKind = 'drop' | 'placed' | 'collapse' | 'gameOver';

const notes: Record<SoundKind, readonly [number, number, number]> = {
  drop: [260, 180, 0.12],
  placed: [520, 720, 0.18],
  collapse: [180, 105, 0.22],
  gameOver: [240, 80, 0.42],
};

function browserAudioFactory(): AudioContextLike {
  const AudioContextConstructor = window.AudioContext;
  return new AudioContextConstructor() as unknown as AudioContextLike;
}

export class GameAudio {
  private context: AudioContextLike | null = null;
  private disabled = false;
  private unlockAttempted = false;

  constructor(private readonly factory: () => AudioContextLike = browserAudioFactory) {}

  async unlock(): Promise<void> {
    if (this.unlockAttempted) return;
    this.unlockAttempted = true;
    try {
      this.context = this.factory();
      if (this.context.state !== 'running') await this.context.resume();
    } catch {
      this.context = null;
      this.disabled = true;
    }
  }

  isDisabled(): boolean {
    return this.disabled;
  }

  play(kind: SoundKind): void {
    if (!this.context || this.disabled) return;
    try {
      const [from, to, duration] = notes[kind];
      const now = this.context.currentTime;
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = kind === 'gameOver' ? 'sawtooth' : 'sine';
      oscillator.frequency.setValueAtTime(from, now);
      oscillator.frequency.exponentialRampToValueAtTime(to, now + duration);
      gain.gain.setValueAtTime(0.055, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
      oscillator.connect(gain);
      gain.connect(this.context.destination);
      oscillator.start(now);
      oscillator.stop(now + duration);
    } catch {
      this.disabled = true;
    }
  }
}

